import {
  FlagCategory,
  FlagPriority,
  FlagStates,
  FlagTypes,
} from '@generated/graphql';
import dayjs from 'dayjs';
import type { Theme } from 'hephaestus/core';
import type { TFunction } from 'i18next';
import type { ReconFlagSeverity } from './reconFlags.types';
export const PAGE_SIZE = 15;

/** Min characters before calling `searchUsers`. */
export const RECON_FLAGS_MIN_SEARCH_LENGTH = 2;

/** Debounce for the client search Autocomplete (ms). */
export const RECON_FLAGS_SEARCH_DEBOUNCE_MS = 400;

/**
 * Max account IDs sent on `fetchFlagsForGrid` from a selected client's accounts.
 * Must stay aligned with flag-service `MAX_ACCOUNT_IDS` (validation rejects more than 50).
 */
export const RECON_FLAGS_MAX_ACCOUNT_IDS = 50;

/** Max users returned from `searchUsers` for the Autocomplete. */
export const RECON_FLAGS_MAX_USER_SEARCH_RESULTS = 12;

export const SPOTLIGHT_TYPES = [
  {
    type: FlagTypes.NegativeCash,
    titleKey: 'reconFlags.spotlight.negativeCash.title',
    colorKey: 'negative' as const,
  },
  {
    type: FlagTypes.NegativeHolding,
    titleKey: 'reconFlags.spotlight.negativeHolding.title',
    colorKey: 'warning' as const,
  },
  {
    type: FlagTypes.DifferentHoldingsV2,
    titleKey: 'reconFlags.spotlight.holdingsMismatch.title',
    colorKey: 'supportThree' as const,
  },
  {
    type: FlagTypes.MoneyAvailableValueIncorrectV2,
    titleKey: 'reconFlags.spotlight.moneyAvailableMismatch.title',
    colorKey: 'positive' as const,
  },
] as const;

export type SpotlightCardColorKey =
  (typeof SPOTLIGHT_TYPES)[number]['colorKey'];

/** Maps KPI spotlight palette keys to `SnapshotCard` tone tokens. */
export const spotlightColorKeyToSnapshotTone = (
  colorKey: SpotlightCardColorKey,
): 'positive' | 'negative' | 'warning' | 'primary' => {
  switch (colorKey) {
    case 'negative':
      return 'negative';
    case 'warning':
      return 'warning';
    case 'supportThree':
      return 'primary';
    case 'positive':
      return 'positive';
    default:
      return 'primary';
  }
};

/** Maps BE `FlagCategory` → `reconFlags.categories.*` i18n key (display only). */
export const FLAG_CATEGORY_TO_I18N: Record<FlagCategory, string> = {
  [FlagCategory.Activity]: 'activity',
  [FlagCategory.Warning]: 'warning',
  [FlagCategory.CashHoldings]: 'cashHoldings',
  [FlagCategory.Snapshot]: 'snapshot',
  [FlagCategory.Transfer]: 'transfer',
  [FlagCategory.Account]: 'account',
  [FlagCategory.Product]: 'product',
  [FlagCategory.Unknown]: 'activity',
};

/** Title-cases `SCREAMING_SNAKE_CASE` when no `reconFlags.flagTypes.*` entry exists. */
const formatFlagTypeFallbackLabel = (flagType: string): string =>
  flagType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/** Human-readable label for a `FlagTypes` enum value (`reconFlags.flagTypes.*`). */
export const getReconFlagTypeLabel = (t: TFunction, flagType: string): string =>
  t(`reconFlags.flagTypes.${flagType}`, {
    defaultValue: formatFlagTypeFallbackLabel(flagType),
  });

/** Sort flag type enum values A–Z by their localized filter label. */
export const sortReconFlagTypesByLabel = (
  t: TFunction,
  flagTypes: readonly string[],
): string[] =>
  [...flagTypes].sort((a, b) =>
    getReconFlagTypeLabel(t, a).localeCompare(getReconFlagTypeLabel(t, b)),
  );

/** Human-readable label for a `FlagStates` enum value (`reconFlags.flagState.*`). */
export const getReconFlagStateLabel = (t: TFunction, state: string): string =>
  t(`reconFlags.flagState.${state}`, { defaultValue: state });

export const RECON_FLAG_STATE_FILTER_VALUES: readonly FlagStates[] = [
  FlagStates.Flagged,
  FlagStates.Fixed,
  FlagStates.Trivial,
];

/** Sort flag state filter values A–Z by their localized label. */
export const sortReconFlagStateFilterValuesByLabel = (
  t: TFunction,
  states: readonly FlagStates[],
): FlagStates[] =>
  [...states].sort((a, b) =>
    getReconFlagStateLabel(t, a).localeCompare(getReconFlagStateLabel(t, b)),
  );

export const FLAG_CATEGORY_FILTER_OPTIONS: readonly {
  value: FlagCategory;
  i18nKey: string;
}[] = [
  { value: FlagCategory.Snapshot, i18nKey: 'snapshot' },
  { value: FlagCategory.Warning, i18nKey: 'warning' },
  { value: FlagCategory.CashHoldings, i18nKey: 'cashHoldings' },
  { value: FlagCategory.Activity, i18nKey: 'activity' },
  { value: FlagCategory.Transfer, i18nKey: 'transfer' },
  { value: FlagCategory.Account, i18nKey: 'account' },
  { value: FlagCategory.Product, i18nKey: 'product' },
];

/** Sort category filter options A–Z by their localized label. */
export const sortReconFlagCategoryFilterOptionsByLabel = (
  t: TFunction,
  options: readonly { value: FlagCategory; i18nKey: string }[],
): { value: FlagCategory; i18nKey: string }[] =>
  [...options].sort((a, b) =>
    t(`reconFlags.categories.${a.i18nKey}`).localeCompare(
      t(`reconFlags.categories.${b.i18nKey}`),
    ),
  );

/** Uses `Flag.priority` from the row for badge colors (no inference from `type`). */
export const badgeSeverityFromPriority = (
  p: FlagPriority | null | undefined,
): ReconFlagSeverity => {
  if (p == null || p === FlagPriority.Unknown) {
    return 'MEDIUM';
  }
  return p as ReconFlagSeverity;
};

// Local calendar-day age, so same-date flags share one age regardless of time.
export const agingDays = (createdAt: string): number =>
  Math.max(
    0,
    dayjs().startOf('day').diff(dayjs(createdAt).startOf('day'), 'day'),
  );

// Inclusive firstSeenAt upper bound for an "age >= days" filter (local days).
export const ageCutoffIso = (days: number): string =>
  dayjs().subtract(days, 'day').endOf('day').toISOString();

/** Age filter thresholds in days; drives the dropdown and the grid filter. */
export const RECON_FLAGS_AGE_FILTER_DAYS = [1, 3, 7] as const;

export type ReconFlagsAgeFilterDays =
  (typeof RECON_FLAGS_AGE_FILTER_DAYS)[number];

export const RECON_FLAGS_AGE_FILTER_I18N: Record<
  ReconFlagsAgeFilterDays,
  string
> = {
  1: 'agingOneDay',
  3: 'agingThreeDays',
  7: 'agingSevenDays',
};

/** Unresolved KPI tile threshold; typed as an Age option so the tile stays
 * reproducible via the dropdown. */
export const UNRESOLVED_TILE_AGE_DAYS: ReconFlagsAgeFilterDays = 3;

export const isReconFlagsAgeFilterDays = (
  days: number | null,
): days is ReconFlagsAgeFilterDays =>
  RECON_FLAGS_AGE_FILTER_DAYS.includes(days as ReconFlagsAgeFilterDays);

/**
 * Snapshot card value/border accent for each KPI spotlight `colorKey`
 * (`spotlightColorKeyToSnapshotTone`).
 */
export const spotlightAccentColor = (
  theme: Theme,
  colorKey: SpotlightCardColorKey,
): string => {
  switch (spotlightColorKeyToSnapshotTone(colorKey)) {
    case 'negative':
      return String(theme.color.negative);
    case 'warning':
      return String(theme.color.warning);
    case 'primary':
      return String(theme.color.primary);
    case 'positive':
      return String(theme.color.positive);
  }
};

const PRIORITY_SEVERITY_SPOTLIGHT_COLOR: Record<
  ReconFlagSeverity,
  SpotlightCardColorKey
> = {
  CRITICAL: 'negative',
  HIGH: 'warning',
  MEDIUM: 'supportThree',
  LOW: 'positive',
};

/**
 * Border + text color for inactive severity filter pills (`SeverityFilterPill`).
 * Use for grid/drawer priority chips so they match the KPI spotlight cards.
 */
export const severityFilterAccentColor = (
  theme: Theme,
  severity: ReconFlagSeverity,
): string =>
  spotlightAccentColor(theme, PRIORITY_SEVERITY_SPOTLIGHT_COLOR[severity]);

/** Same surface + outlined pill as inactive severity filters (border and text = accent). */
export const getReconFlagPriorityBadgeSx = (
  theme: Theme,
  severity: ReconFlagSeverity,
) => {
  const accent = severityFilterAccentColor(theme, severity);
  return {
    color: accent,
    backgroundColor: String(theme.color.surface),
    borderColor: accent,
    borderStyle: 'solid' as const,
    borderWidth: '1px',
  };
};

/** State pills: flagged = critical accent; fixed/trivial (“done”) = low; else outline. */
export const getReconFlagStateBadgeSx = (theme: Theme, state: string) => {
  if (state === FlagStates.Flagged) {
    return getReconFlagPriorityBadgeSx(theme, 'CRITICAL');
  }
  if (state === FlagStates.Fixed || state === FlagStates.Trivial) {
    return getReconFlagPriorityBadgeSx(theme, 'LOW');
  }
  const accent = String(theme.color.outline);
  return {
    color: accent,
    backgroundColor: String(theme.color.surface),
    borderColor: accent,
    borderStyle: 'solid' as const,
    borderWidth: '1px',
  };
};

const spotlightGradient = (from: string, to: string): string =>
  `linear-gradient(135deg, ${from} 0%, ${to} 150%)`;

/**
 * Full `background` value for KPI spotlight cards (overrides flat card fill).
 * Stops follow semantic palette main → darker for left-to-right severity styling.
 */
export const spotlightCardBackground = (
  theme: Theme,
  colorKey: SpotlightCardColorKey,
): string => {
  const surface =
    theme.color.surfaceContainer ?? theme.color.surface ?? '#ffffff';

  switch (colorKey) {
    case 'negative': {
      const from = theme.color.negative ?? surface;
      const to = theme.color.negativePressed ?? theme.color.negative ?? from;
      return spotlightGradient(from, to);
    }
    case 'warning': {
      const from = theme.color.warning ?? surface;
      const to = theme.color.warningPressed ?? theme.color.warning ?? from;
      return spotlightGradient(from, to);
    }
    case 'supportThree': {
      const from = theme.color.supportThree ?? theme.color.warning ?? surface;
      const to =
        theme.color.supportThreeOnVariant ??
        theme.color.warningPressed ??
        theme.color.warning ??
        from;
      return spotlightGradient(from, to);
    }
    case 'positive': {
      const from = theme.color.positive ?? surface;
      const to = theme.color.positivePressed ?? theme.color.positive ?? from;
      return spotlightGradient(from, to);
    }
    default:
      return spotlightGradient(surface, surface);
  }
};

export const spotlightOnColor = (
  theme: Theme,
  colorKey: SpotlightCardColorKey,
): string | undefined => {
  switch (colorKey) {
    case 'negative':
      return theme.color.onNegative ?? theme.color.onSurface;
    case 'warning':
      return theme.color.onWarning ?? theme.color.onSurface;
    case 'supportThree':
      return theme.color.onWarning ?? theme.color.onSurface;
    case 'positive':
      return theme.color.onPositive ?? theme.color.onSurface;
    default:
      return theme.color.onSurface;
  }
};

/** Filled surface colors for type pills keyed off flag priority. */
export const getReconFlagPriorityFilledSurface = (
  theme: Theme,
  priority: FlagPriority | null | undefined,
): { bgcolor: string; color: string } => {
  if (priority == null || priority === FlagPriority.Unknown) {
    return {
      bgcolor: String(theme.color.outline ?? '#c4c4c4'),
      color: String(theme.color.onSurface ?? '#262626'),
    };
  }
  const severity = badgeSeverityFromPriority(priority);
  const colorKey = PRIORITY_SEVERITY_SPOTLIGHT_COLOR[severity];
  return {
    bgcolor: severityFilterAccentColor(theme, severity),
    color: String(spotlightOnColor(theme, colorKey) ?? theme.color.onSurface),
  };
};
